import {
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { RequestVirtualCardScreen } from 'src/app/RequestVirtualCard/RequestVirtualCard'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import Button from 'src/components/Button/Button'
import ChooseObjectTypeBox from 'src/components/ChooseObjectTypeBox/ChooseObjectTypeBox'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import {
  registerOrganizationUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import styled, { css, CSSProp } from 'styled-components'

interface RequestVirtualCardPickContentProps {
  setScreen: (val: RequestVirtualCardScreen) => void
  standardDisabled?: boolean
  recurringDisabled?: boolean
  userHasOrg?: boolean
  userHasActiveRCNs?: boolean
  hideRecurring?: boolean
}

const ChooseCreateObjectTypeBox = styled(ChooseObjectTypeBox)<{
  disabled?: boolean
}>`
  ${({ disabled }): CSSProp => {
    return disabled
      ? css`
          border: 1px solid ${styleConstants.colors.yellow_dark};
        `
      : css``
  }}

  &:nth-child(n+2) {
    margin-top: ${styleConstants.heights.minorInternalGutter};
  }
`

const RequestVirtualCardPickContent: React.FC<
  RequestVirtualCardPickContentProps
> = (props) => {
  const standardCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeStandard,
  )
  const recurringCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurring,
  )
  const { analytics } = useAnalyticsContext()

  const disabled = !props.userHasOrg

  return (
    <ActionRightFlyoutContent
      title={<Trns source={'Request a Virtual Card'} />}
      pickerSubtitle={
        <Trns
          source={
            "Choose the type of card you need to use. Once it's approved, you can use it right away!"
          }
        />
      }
    >
      {disabled && (
        <SoftAlertBox>
          <div
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <div
              css={css`
                font-family: ${styleConstants.fontFamilies.medium};
              `}
            >
              <Trns source={'Card types are changing!'} />
            </div>
            <div
              css={css`
                margin-top: 20px;
              `}
            >
              {props.userHasActiveRCNs ? (
                <>
                  <Trns
                    source={
                      "Please confirm your org and team to keep using these cards (we've filled things out for you to speed things up)."
                    }
                  />
                  <div
                    css={css`
                      display: flex;
                    `}
                  >
                    <Button
                      css={css`
                        margin-top: 20px;
                      `}
                      linkProps={{
                        to: registerOrganizationUrl(),
                        ariaLabelSource: 'Review and confirm org',
                      }}
                      design={ButtonDesigns.designCta}
                      type={ButtonTypes.button}
                      text={<Trns source={'Review and confirm org'} />}
                    />
                  </div>
                </>
              ) : (
                <Trns
                  source={
                    'You will need to be in an organization in order to request cards. Contact your admin to get invited to their org.'
                  }
                />
              )}
            </div>
          </div>
        </SoftAlertBox>
      )}
      <ChooseCreateObjectTypeBox
        imageSrc={standardCardImage}
        id={'standardCard'}
        title={<Trns source={'Standard Card'} />}
        subtitle={
          <Trns
            source={
              'The most common option. You can receive or request card updates at any time.'
            }
          />
        }
        onClick={(): void => {
          props.setScreen(RequestVirtualCardScreen.Standard)
          analytics?.track(
            'request virtual card flyout - request standard virtual card',
          )
        }}
        disabled={props.standardDisabled || disabled}
      />
      {!props.hideRecurring && (
        <ChooseCreateObjectTypeBox
          imageSrc={recurringCardImage}
          id={'recurringCard'}
          title={<Trns source={'Recurring Card'} />}
          subtitle={
            <Trns
              source={
                'Have a recurring expense? This virtual card will reset its spend limit at a daily, weekly or monthly frequency.'
              }
            />
          }
          onClick={(): void => {
            props.setScreen(RequestVirtualCardScreen.Recurring)
            analytics?.track(
              'request virtual card flyout - request recurring virtual card - button press',
              {
                interaction: 'button press',
                intent: 'request recurring virtual card',
                screen: requestVirtualCardUrl(),
              },
            )
          }}
          disabled={props.recurringDisabled || disabled}
        />
      )}
    </ActionRightFlyoutContent>
  )
}

export default RequestVirtualCardPickContent
